import { planLabel } from "@contract/firm/plan";
import type { Me } from "@contract/firm/me";
import { Anchor, Button, Group, Text, TextInput } from "@mantine/core";
import * as React from "react";
import { track } from "../../analytics/analytics";
import { ApiError } from "../../api/client";
import { renameFirm } from "../../api/me";
import { SecondaryButton } from "../../ui/buttons";
import { DetailRow, InlineEdit } from "../../ui/DetailRow";
import { Notice } from "../../ui/Notice";
import { SectionCard } from "../../ui/SectionCard";
import { firmNamed, planRowText, teamLine } from "../homeModel";

/**
 * Home's Your Details card (A8, Dan's frame 15187:6107): Company Name, Team Members and Plan.
 * Admins who manage members rename the firm here (`PUT /v1/firm/name`) and see Team Members;
 * everyone else reads the name and the plan.
 */
export function YourDetailsCard({
  me,
  now,
  onRenamed,
  rename = renameFirm,
}: {
  me: Me;
  now: Date;
  /** Called after a save, so /v1/me picks up the new name. */
  onRenamed: () => void;
  rename?: typeof renameFirm;
}) {
  const canRename = me.capabilities.includes("manage_members");
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const startEditing = () => {
    // A placeholder name opens empty: there's nothing of the admin's to edit yet.
    setDraft(firmNamed(me) ? me.orgName : "");
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await rename(draft.trim());
      track("web_firm_renamed");
      setEditing(false);
      onRenamed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "We couldn't save the name. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Your Details" gap={8}>
      <DetailRow label="Company Name">
        {editing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.trim()) void save();
            }}
          >
            <Group gap={5} wrap="nowrap">
              <TextInput
                aria-label="Company Name"
                placeholder="Your firm's name"
                maxLength={80}
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
                style={{ flex: 1 }}
                data-autofocus
                autoFocus
              />
              <SecondaryButton size="xs" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </SecondaryButton>
              <Button size="xs" type="submit" loading={saving} disabled={!draft.trim()}>
                Save
              </Button>
            </Group>
          </form>
        ) : (
          <Text lh={1.25}>
            <Text span inherit c={firmNamed(me) ? undefined : "dimmed"}>
              {firmNamed(me) ? me.orgName : "Not named yet"}
            </Text>
            {canRename ? (
              <InlineEdit>
                <Anchor component="button" type="button" fw={400} inherit onClick={startEditing} aria-label="Edit the company name">
                  (Edit)
                </Anchor>
              </InlineEdit>
            ) : null}
          </Text>
        )}
      </DetailRow>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {canRename ? (
        <DetailRow label="Team Members">
          <Text lh={1.25}>{teamLine(me.seats)}</Text>
        </DetailRow>
      ) : null}
      <DetailRow label="Plan">
        <Text lh={1.25}>{planRowText(planLabel(me.plan, me.isBeta), me, now)}</Text>
      </DetailRow>
    </SectionCard>
  );
}
